// src/contexts/LocaleContext.tsx - account-synced with localStorage fallback
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { userPreferencesAPI } from '../services/api';

export type LocaleType = string;

// Single source of truth for which locales exist and their display labels.
// Extend this list as more languages are added - the /profile language
// picker (a CustomSelect) is driven directly off this array.
export const AVAILABLE_LOCALES: { code: LocaleType; label: string }[] = [
  { code: 'en-ZA', label: 'English' },
  { code: 'nl-NL', label: 'Nederlands' },
];

const DEFAULT_LOCALE: LocaleType = 'en-ZA';

const isKnownLocale = (value: string | null): value is LocaleType =>
  AVAILABLE_LOCALES.some((l) => l.code === value);

interface LocaleContextType {
  locale: LocaleType;
  setLocale: (locale: LocaleType) => void;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const useLocale = (): LocaleContextType => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

interface LocaleProviderProps {
  children: React.ReactNode;
}

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
  const { user, isAuthenticated, updateUserPrefs } = useAuth();

  // Seed from localStorage so the pre-login screen isn't stuck on a
  // hardcoded default - this is just a device-level "last known" value,
  // not the source of truth once an account is involved.
  const [locale, setLocaleState] = useState<LocaleType>(() => {
    try {
      const saved = localStorage.getItem('appLocale');
      return isKnownLocale(saved) ? saved : DEFAULT_LOCALE;
    } catch {
      return DEFAULT_LOCALE;
    }
  });

  // Once authenticated, the account's saved preference takes over - and
  // mirrors into localStorage so the next logged-out view of this device
  // reflects this user's last choice.
  useEffect(() => {
    if (isAuthenticated && user?.locale && isKnownLocale(user.locale)) {
      setLocaleState(user.locale);
      try {
        localStorage.setItem('appLocale', user.locale);
      } catch {
        // ignore storage errors
      }
    }
  }, [isAuthenticated, user?.locale]);

  const setLocale = (newLocale: LocaleType) => {
    setLocaleState(newLocale);
    try {
      localStorage.setItem('appLocale', newLocale);
    } catch {
      // ignore storage errors
    }

    if (isAuthenticated && user) {
      // Applied immediately, not gated behind the PUT resolving - same
      // race as ThemeContext.setThemeMode had (see its comment for the
      // full explanation): AuthContext rehydrates `user` from the
      // persisted authUser blob on the next mount, and this provider's
      // own sync effect above trusts user.locale as authoritative once
      // authenticated. A reload landing before the API call resolved
      // would otherwise silently revert a locale change that had
      // already taken visible effect.
      updateUserPrefs({ locale: newLocale });

      userPreferencesAPI
        .updateMessagePreferences({
          msgBackupAlerts: user.msgBackupAlerts,
          msgQuoteAlerts: user.msgQuoteAlerts,
          msgBookingReminders: user.msgBookingReminders,
          msgSystemToasts: user.msgSystemToasts,
          accessibilityMode: user.accessibilityMode,
          showPerformanceIndicator: user.showPerformanceIndicator,
          showOnboarding: user.showOnboarding ?? true,
          themeMode: user.themeMode,
          locale: newLocale,
        })
        .catch(() => {
          // Best-effort sync - the local/localStorage value still applies
          // for this session even if the account save fails.
        });
    }
  };

  const contextValue: LocaleContextType = {
    locale,
    setLocale,
  };

  return (
    <LocaleContext.Provider value={contextValue}>
      {children}
    </LocaleContext.Provider>
  );
};
